import { signal, computed, effect } from "@preact/signals-core";
import { cartData, totalPrice, type CartItem } from "./cart-store";
import { getProductByHandle } from "../utils/knocking";

/**
 * Retail (compare-at) pricing for the cart.
 *
 * Shopify's /cart.js line items carry no `compare_at_price`, so it is hydrated
 * per product from /products/{handle}.js (CDN-cached) — one request per
 * distinct product handle, never repeated within a page load.
 *
 * "Retail" for a line is the higher of the variant's compare-at price and the
 * line's pre-discount `original_price`, so Shopify discounts and merchant
 * compare-at prices both read as savings, and a line with neither contributes
 * zero (mixed carts never show a misleading saving).
 */

/** variant id → compare-at price in cents (only variants that have one). */
export const compareAtByVariant = signal<Record<number, number>>({});

const requestedHandles = new Set<string>();

const hydrateHandle = async (handle: string): Promise<void> => {
  requestedHandles.add(handle);
  const product = await getProductByHandle(handle);
  if (!product) return;

  const next: Record<number, number> = {};
  for (const variant of product.variants) {
    if (variant.compare_at_price && variant.compare_at_price > 0) {
      next[variant.id] = variant.compare_at_price;
    }
  }
  compareAtByVariant.value = { ...compareAtByVariant.value, ...next };
};

/**
 * Retail unit price (cents) for a cart line. Lines merged by a Cart Transform
 * (`has_components`) fold a fee variant into `original_price`, so the main
 * variant's compare-at cannot be compared against it — they fall back to
 * their pre-discount price.
 */
export const getRetailUnitPrice = (
  item: CartItem,
  compareAt: Record<number, number>
): number => {
  if (item.has_components) return item.original_price;
  return Math.max(compareAt[item.variant_id] ?? 0, item.original_price);
};

/** Sum of retail unit price × quantity across all lines, in cents. */
export const retailSubtotal = computed(() => {
  const items = cartData.value?.items ?? [];
  const compareAt = compareAtByVariant.value;
  return items.reduce(
    (sum, item) => sum + getRetailUnitPrice(item, compareAt) * item.quantity,
    0
  );
});

/** Total saving vs retail, in cents. Never negative; 0 means "show nothing". */
export const totalSavings = computed(() =>
  Math.max(0, retailSubtotal.value - totalPrice.value)
);

let started = false;

/** Start hydrating compare-at prices whenever the cart gains a new product. */
export const initRetailPrices = (): void => {
  if (started) return;
  started = true;
  effect(() => {
    for (const item of cartData.value?.items ?? []) {
      if (item.handle && !requestedHandles.has(item.handle)) {
        void hydrateHandle(item.handle);
      }
    }
  });
};

/** Test helper: forget hydrated data and in-flight bookkeeping. */
export const resetRetailPrices = (): void => {
  requestedHandles.clear();
  compareAtByVariant.value = {};
};
